import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

// Запоминает прокрутку страницы и возвращает её, когда человек вернулся кнопкой «назад» (например, из игры на главную).
// На переходах вперёд страница открывается сверху. Позиция хранится на время вкладки (sessionStorage).
const prefix = 'scroll:';

export function readScroll(key: string): number {
  try {
    const value = Number(sessionStorage.getItem(prefix + key));
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

export function writeScroll(key: string, value: number): void {
  try {
    sessionStorage.setItem(prefix + key, String(Math.max(0, Math.round(value))));
  } catch {
    // хранилище недоступно: позиция просто не запомнится
  }
}

// selector — элемент, который прокручивается (у каждой страницы он свой); ready — данные страницы уже на экране
export function useScrollMemory(selector: string, ready = true): void {
  const { pathname, search } = useLocation();
  const type = useNavigationType();
  const key = `${pathname}${search}`;
  const restored = useRef(false);

  // Возврат позиции: только при переходе «назад», с несколькими попытками, пока контент дорисовывается
  useEffect(() => {
    if (!ready || restored.current) return undefined;
    const target = type === 'POP' ? readScroll(key) : 0;
    if (target === 0) {
      restored.current = true;
      return undefined;
    }
    let frame = 0;
    let attempts = 0;
    const apply = () => {
      const element = document.querySelector<HTMLElement>(selector);
      if (element) {
        element.scrollTop = target;
        if (element.scrollHeight - element.clientHeight >= target - 2 || attempts >= 30) {
          restored.current = true; // отмечаем только после настоящего возврата: в режиме разработки эффект запускается дважды
          return;
        }
      }
      attempts += 1;
      frame = requestAnimationFrame(apply);
    };
    frame = requestAnimationFrame(apply);
    return () => cancelAnimationFrame(frame);
  }, [ready, key, selector, type]);

  // Запись позиции при прокрутке (не чаще раза в 150 мс). Слушаем на документе в фазе перехвата: так не важно,
  // когда именно появился прокручиваемый блок, а при уходе со страницы пишем последнее увиденное значение (блок уже убран из DOM)
  useEffect(() => {
    let timer: number | undefined;
    let last: number | null = null;
    const onScroll = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || !target.matches(selector)) return;
      last = target.scrollTop;
      if (timer !== undefined) return;
      timer = window.setTimeout(() => {
        timer = undefined;
        if (last !== null) writeScroll(key, last);
      }, 150);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener('scroll', onScroll, { capture: true });
      if (timer !== undefined) window.clearTimeout(timer);
      if (last !== null) writeScroll(key, last);
    };
  }, [key, selector]);
}
